import { IconButton } from '@/components/buttons/Buttons';
import EmptyTable from '@/components/empty/EmptyTable';
import SearchBarInput from '@/components/form-inputs/SearchBarInput';
import { Table } from '@/components/table/Tables';
import ConfirmationDialogHeader from '@/modules/confirmation/components/ConfirmationDialogHeader';
import { showConfirmationDialog } from '@/modules/confirmation/engines/confirmation.library';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { Column } from 'primereact/column';
import type { DataTableValue } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { Toolbar } from 'primereact/toolbar';
import { classNames } from 'primereact/utils';
import { useEffect, useRef, useState } from 'react';
import { MdDelete, MdEdit, MdPersonAdd } from 'react-icons/md';
import style from './group.module.css';
import StatusTag from '../components/StatusTag';
import AddGroupExistingPeopleScreen from './AddGroupExistingPeopleScreen';
import AddGroupNewPeopleScreen from './AddGroupNewPeopleScreen';
import { useQuery } from '@tanstack/react-query';
import { BaseResponse, GenericResponse, ListRequest, ListResponse, showTypedModal2 } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { axiosInstance } from '@/services/axios.service';
import { getEmployeeStatusString, X4bEmployeeListItem, X4bEmployeeStatus } from '@/types';
import { GetGroupResponse } from './GroupInfoScreen';
import { useTableState } from '@/common/useTableState';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

type People = {
	name: string;
	status: string;
	email: string;
	id: string;
};

type Option = { name: string; code: string; };
const defaultStatusOptions: Option[] = [
	{ name: 'All', code: 'All' },
	{ name: 'Active', code: 'Active' },
	{ name: 'Invited', code: 'Invited' },
];

interface GetGroupEmployeesRequest extends ListRequest {
	GroupId: number;
}

interface RemoveEmployeesRequest {
	groupId: number;
	peopleIds: number[];
}

const ListGroupPeopleScreen = ({ group }: { group: GetGroupResponse }) => {
	const [selectedPeoples, setSelectedPeoples] = useState<X4bEmployeeListItem[]>([]);
	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]);
	// const [search, setSearch] = useState<string>('');

	const menu = useRef<Menu>(null);

	const [statusOptions, setStatusOptions] = useState<Option[]>(defaultStatusOptions);
	const { sort, filter, updateFilter, tableProps } = useTableState({ skip: 0, take: 5, nameEmailFilter: '', statusFilter: defaultStatusOptions[0] });

	const filterKey = { ...filter, statusFilter: (({ name, ...rest }) => rest)(filter.statusFilter) };

	const { data: people, isFetching, refetch } = useQuery({
		queryKey: ['ListGroupPeopleScreen.GetGroupEmployees', filterKey, sort],
		initialData: { Items: [] as X4bEmployeeListItem[], TotalRecords: 0 },
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bEmployeeListItem>>>(ENDPOINTS['/api/X4bGroup/GetGroupEmployees'], {
				SortField: sort.sortField ?? "",
				SortOrder: sort.sortOrder,
				Skip: filter.skip,
				Take: filter.take,
				GroupId: group.Id,
				Filters: [
					{ Name: "Status", Value: filter.statusFilter.code == "All" ? "" : filter.statusFilter.code },
					{ Name: "NameEmail", Value: filter.nameEmailFilter }
				]
			} as GetGroupEmployeesRequest);
			return result.data.Data;
		}
	});

	useEffect(() => {
		if (!isFetching && people) {
			setStatusOptions(defaultStatusOptions.map(option => {
				if (option.code !== filter.statusFilter.code) return option;
				return { code: option.code, name: `${option.name} (${people.TotalRecords})` };
			}));

			updateFilter({
				statusFilter: {
					code: filter.statusFilter.code,
					name: `${defaultStatusOptions.find(o => o.code === filter.statusFilter.code)?.name ?? filter.statusFilter.name} (${people.TotalRecords})`,
				}
			});
		}
	}, [people, isFetching]);


	const items: MenuItem[] = [
		{
			label: 'Add existing people', command: async () => {
				await showTypedModal2(AddGroupExistingPeopleScreen, { groupId: group.Id });
				await refetch();
			}
		},
		{
			label: 'Add new people', command: async () => {
				await showTypedModal2(AddGroupNewPeopleScreen, { group });
				await refetch();
			}
		},
	];


	const deleteHeaderTitle = selectedPeoples && selectedPeoples.length === 1
		? 'Remove from Product Team?'
		: 'Delete selected employees?';
	const deleteMessage = selectedPeoples && selectedPeoples.length > 1
		? 'Are you sure you want to delete these employees? This action cannot be undone.'
		: 'Are you sure you want to remove this employee? This action cannot be undone.';

	const deleteHeaderTemplate = (
		<ConfirmationDialogHeader
			message={deleteHeaderTitle}
			severity="warning"
		/>
	);

	const handleDeletePeople = () => showConfirmationDialog(deleteMessage, {
		accept: async () => {
			const response = await axiosInstance.post<BaseResponse>(
				'/api/X4bGroup/RemoveEmployees', {
					groupId: group.Id,
					peopleIds: selectedPeoples.map(p => p.Id),
				} as RemoveEmployeesRequest
			);

			if (!response.data.Success)
				showNotification('Failed to delete employees');

			let successMessage = '';

			if (selectedPeoples.length) {
				successMessage = selectedPeoples.length > 1
					? 'Selected employees deleted successfully.'
					: `${selectedPeoples[0].FirstName} ${selectedPeoples[0].LastName} has been removed.`;
			}

			showNotification(successMessage);
			await refetch();
		},
		acceptLabel: 'Delete',
		rejectLabel: 'Cancel',
		header: deleteHeaderTemplate,
		acceptClassName: '!bg-error',
	});

	const statusBodyTemplate = (rowData: X4bEmployeeListItem) => (
		<StatusTag
			value={getEmployeeStatusString(rowData.Status)}
			severity={`${rowData.Status === X4bEmployeeStatus.Active ? 'success' : 'warning'}`}
		/>
	);

	const startContent = (
		<div className="flex gap-6">
			<Dropdown
				value={filter.statusFilter}
				onChange={(e) => updateFilter({ statusFilter: e.value })}
				options={statusOptions}
				className="w-full min-w-44 h-14"
				optionLabel="name"
				pt={{
					root: {
						className: 'people-dropdown',
					},
					panel: {
						className: 'people-dropdown-content',
					},
				}}
			/>

			<SearchBarInput
				placeholder="Search by name or email"
				onChange={(e) => updateFilter({ nameEmailFilter: e.target.value })}
				className="w-96 shrink-0"
			/>

			{/* TODO[Phase2] */}
			{/* <AutoCompleteSearch
				suggestions={people}
				placeholder="Search by name or email"
				onSelect={handleSearchSelect}
				onChange={handleSearchChange}
				filterMethod={searchFilter}
				label="name"
				description="email"
				value={search}
				className="md:w-96 h-14"
			/> */}
		</div>
	);

	const selectedActionButtons = (
		<div className="flex gap-3">
			{false && (
				<IconButton
					id="people-edit"
					label="Edit"
					icon={<MdEdit className="mr-2" />}
					className="secondary h-10"
					rounded
				/>
			)}
			<IconButton
				id="people-delete"
				label="Delete"
				icon={<MdDelete className="mr-2" />}
				className="secondary h-10"
				rounded
				onClick={handleDeletePeople}
			/>
		</div>
	);

	const endContent = selectedPeoples.length ? (
		selectedActionButtons
	) : (
		<div className="flex">
			<IconButton
				id="people-add"
				label="Add people"
				icon={<MdPersonAdd className="mr-2" />}
				className="text-white h-10"
				rounded
				onClick={(event) => menu.current?.toggle(event)}
				aria-controls="people-add"
				aria-haspopup="true"
			/>
			<Menu
				model={items}
				popup
				ref={menu}
				id="people-add"
				pt={{
					root: {
						className: classNames(style.groupMenuAdd),
					},
				}}
			/>
		</div>
	);

	// TODO table height when no data
	return (
		<div>
			<Toolbar
				pt={{
					root: { className: 'people-toolbar mb-6' },
				}}
				start={startContent}
				end={isManagerOrAdmin ? endContent : (<></>)}
			/>
			<Table
				size="small"
				loading={isFetching}
				value={people.Items}
				emptyMessage={EmptyTable({
					title: 'No employees have been added yet',
					src: '/images/svg/Empty State - No Employees.svg',
				})}
				selectionMode={'checkbox'}
				paginatorClassName={style.groupTablePagination}
				pt={{ wrapper: { className: style.groupTableWrapper } }}
				selection={selectedPeoples}
				onSelectionChange={(e: DataTableValue) => setSelectedPeoples(e.value)}
				dataKey="Id"
				lazy
				paginator={people.TotalRecords > 0}
				totalRecords={people.TotalRecords}

				removableSort={true}
				rowsPerPageOptions={[5, 10, 25, 50]}
				{...tableProps}
			>
				<Column selectionMode="multiple" style={{ textAlign: 'center', justifyItems: 'center' }} />
				<Column field="FullName" header="Name" sortable />
				<Column field="StatusName" header="Status" sortable body={statusBodyTemplate} />
				<Column field="Email" header="Email" sortable />
				<Column field="ReferenceCode" header="Employee ID" sortable />
			</Table>
		</div>
	);
};

export default ListGroupPeopleScreen;
